import type { UseCaseOverview } from "@/services/adminModelCurrency";
import AssignmentDetails from "./AssignmentDetails";
import CandidateHistory from "./CandidateHistory";
import CandidateRow from "./CandidateRow";
import PriceLine from "./PriceLine";
import ProviderChip from "./ProviderChip";
import RollbackAction from "./RollbackAction";
import { sourceLabel } from "./helpers";
import { splitCandidates } from "./layoutHelpers";

interface UseCaseCardProps {
  useCase: UseCaseOverview;
  writable: boolean;
  onPromote: (candidateId: string) => Promise<unknown>;
  onDismiss: (candidateId: string) => Promise<unknown>;
  onRunTest: (candidateId: string) => Promise<unknown>;
  onRollback: (useCaseId: string) => Promise<unknown>;
  onResetBaseline: (useCaseId: string) => Promise<unknown>;
}

/** One feature: what it runs on the left, newer models to decide on the right. */
const UseCaseCard = ({
  useCase,
  writable,
  onPromote,
  onDismiss,
  onRunTest,
  onRollback,
  onResetBaseline,
}: UseCaseCardProps) => {
  const currentModel = useCase.effective.model;
  const { open, history } = splitCandidates(useCase.candidates, currentModel);
  return (
    <section className="grid gap-4 p-4 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] md:gap-6">
      <div className="min-w-0 space-y-2">
        <h3 className="text-sm font-semibold">{useCase.label}</h3>
        <div className="space-y-1">
          <p className="break-all font-mono text-sm">{useCase.effective.model}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <ProviderChip provider={useCase.effective.provider} />
            <span className="text-xs text-muted-foreground">
              {sourceLabel(useCase.effective.source)}
            </span>
          </div>
          <PriceLine price={useCase.effective.price} />
        </div>
        {!useCase.hasEvalHarness ? (
          <p className="text-xs text-muted-foreground">Detect-only: no automatic test yet</p>
        ) : null}
        {useCase.assignment ? (
          <>
            <AssignmentDetails
              assignment={useCase.assignment}
              useCaseId={useCase.id}
              writable={writable}
              onResetBaseline={onResetBaseline}
            />
            <RollbackAction
              useCaseId={useCase.id}
              useCaseLabel={useCase.label}
              writable={writable}
              onRollback={onRollback}
            />
          </>
        ) : null}
      </div>
      <div className="min-w-0 space-y-3">
        {open.length === 0 ? (
          <p className="text-sm text-muted-foreground md:pt-0.5">No newer models waiting.</p>
        ) : (
          <ul className="space-y-2">
            {open.map(candidate => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                currentModel={currentModel}
                writable={writable}
                onPromote={onPromote}
                onDismiss={onDismiss}
                onRunTest={onRunTest}
              />
            ))}
          </ul>
        )}
        <CandidateHistory
          candidates={history}
          currentModel={currentModel}
          writable={writable}
          onPromote={onPromote}
          onDismiss={onDismiss}
          onRunTest={onRunTest}
        />
      </div>
    </section>
  );
};

export default UseCaseCard;
